import type { ReactNode } from 'react';

import { cn } from '../lib/cn';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { Pagination } from './Pagination';
import { SkeletonTable } from './Skeleton';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  /** The allowlisted field name the API accepts. Not a display label. */
  field: string;
  direction: SortDirection;
}

export interface Column<T> {
  /** Stable key. Also the sort field when `sortable` is set. */
  key: string;
  header: ReactNode;
  /** Cell content. Return a node; do not return raw HTML. */
  render: (row: T) => ReactNode;
  /**
   * Sorting is server-side and the API allowlists which fields may be sorted
   * (Spec §5). Only set this for a field that is actually on the allowlist —
   * otherwise the request comes back 400.
   */
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  /** Tailwind width utility, e.g. `w-40`. */
  width?: string;
  /** Hidden below `md`. Use for secondary columns rather than horizontal scroll. */
  hideOnMobile?: boolean;
}

export interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  caption?: string;

  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;

  sort?: SortState;
  onSortChange?: (sort: SortState) => void;

  onRowClick?: (row: T) => void;
  /** Highlights a row, e.g. the record open in a side panel. */
  isRowActive?: (row: T) => boolean;

  emptyTitle?: string;
  emptyDescription?: ReactNode;
  emptyAction?: ReactNode;

  pagination?: {
    hasNext: boolean;
    hasPrevious: boolean;
    onNext: () => void;
    onPrevious: () => void;
    summary?: string;
  };

  className?: string;
}

const alignments = { left: 'text-left', right: 'text-right', center: 'text-center' };

function SortIcon({ direction }: { direction: SortDirection | null }) {
  return (
    <svg className="h-3 w-3 shrink-0" viewBox="0 0 12 12" aria-hidden="true">
      <path
        d="M6 2.5 8.5 5.5h-5L6 2.5Z"
        fill="currentColor"
        opacity={direction === 'asc' ? 1 : 0.28}
      />
      <path
        d="M6 9.5 3.5 6.5h5L6 9.5Z"
        fill="currentColor"
        opacity={direction === 'desc' ? 1 : 0.28}
      />
    </svg>
  );
}

/**
 * The list table.
 *
 * Loading, empty and error are states of this component rather than three things a
 * screen assembles by hand, so every list in the product behaves the same way and
 * nobody renders an empty table body with a spinner floating over it.
 *
 * Sorting and pagination are SERVER-side. This component reports intent through
 * `onSortChange` and the pagination callbacks; it never sorts or slices `rows`
 * itself. Client-side sorting over a cursor-paginated, scope-filtered list would
 * sort one page and look, convincingly, like a bug in the data.
 */
export function Table<T>({
  columns,
  rows,
  rowKey,
  caption,
  loading,
  error,
  onRetry,
  sort,
  onSortChange,
  onRowClick,
  isRowActive,
  emptyTitle = 'Nothing to show',
  emptyDescription,
  emptyAction,
  pagination,
  className,
}: TableProps<T>) {
  const showBody = !loading && !error && rows.length > 0;

  const toggleSort = (field: string) => {
    if (!onSortChange) return;
    const direction: SortDirection =
      sort?.field === field && sort.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ field, direction });
  };

  return (
    <div className={cn('overflow-hidden rounded-lg border bg-surface shadow-sm', className)}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-base">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr className="border-b bg-surface-sunken">
              {columns.map((column) => {
                const active = sort?.field === column.key;
                const direction = active ? sort.direction : null;
                return (
                  <th
                    key={column.key}
                    scope="col"
                    // `aria-sort` is how a screen reader announces the sort state.
                    aria-sort={
                      active ? (direction === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                    className={cn(
                      'px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-ink-muted',
                      alignments[column.align ?? 'left'],
                      column.width,
                      column.hideOnMobile && 'hidden md:table-cell',
                    )}
                  >
                    {column.sortable && onSortChange ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(column.key)}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded hover:text-ink',
                          column.align === 'right' && 'flex-row-reverse',
                          active && 'text-ink',
                        )}
                      >
                        {column.header}
                        <SortIcon direction={direction} />
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          {showBody && (
            <tbody className="divide-y">
              {rows.map((row) => {
                const interactive = Boolean(onRowClick);
                return (
                  <tr
                    key={rowKey(row)}
                    onClick={interactive ? () => onRowClick?.(row) : undefined}
                    onKeyDown={
                      interactive
                        ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onRowClick?.(row);
                            }
                          }
                        : undefined
                    }
                    // A clickable row must be reachable and operable by keyboard.
                    // If a row navigates, put a real link in the first cell too —
                    // that is what gives middle-click and "open in new tab".
                    tabIndex={interactive ? 0 : undefined}
                    role={interactive ? 'button' : undefined}
                    className={cn(
                      'transition-colors',
                      interactive && 'cursor-pointer hover:bg-surface-sunken',
                      isRowActive?.(row) && 'bg-primary-subtle',
                    )}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={cn(
                          'px-4 py-2.5 text-ink',
                          alignments[column.align ?? 'left'],
                          column.hideOnMobile && 'hidden md:table-cell',
                        )}
                      >
                        {column.render(row)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </div>

      {loading && <SkeletonTable rows={6} columns={columns.length} />}
      {!loading && Boolean(error) && <ErrorState error={error} onRetry={onRetry} size="sm" />}
      {!loading && !error && rows.length === 0 && (
        <EmptyState
          size="sm"
          title={emptyTitle}
          description={emptyDescription}
          action={emptyAction}
        />
      )}

      {pagination && showBody && <Pagination {...pagination} loading={loading} />}
    </div>
  );
}
